import InputTypesEnum from '../../../Enums/InputTypesEnum';
import PlanningStatusEnum from '../../../Enums/PlanningStatusEnum';
import PlanningDetailsItemService from '../../../Services/PlanningDetailsItemService';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import mathHelper from '../../../Helpers/mathHelper';
import PlanningDetailEditItem from '../../../Domain/PlanningDetailEditItem';

const datesLocked = (item) => !!item.IsDateLocked;
const dateLockInfo = (item) => (item.IsDateLocked ? 'Dates are locked.' : undefined);
const isTpPlatform = (item) => item.MysteryShoppingPlatformPricingId != null;

const shiftDays = (date, days) => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

const rateValidityChecker = (value, editedItem) => {
  if (!editedItem || !editedItem.ApplyTargetRates) return '';
  return mathHelper.Sum(value) === 100 ? '' : 'The rates must add up to 100%.';
};

const rateFieldsDisabled = (item) => !item.ApplyTargetRates;

export default class PlanningDetailEditWindowDescription {
  static titleForCreate = 'Edit PlanningDetail Item';
  static titleForEdit = 'Edit PlanningDetail Item';
  static itemType = PlanningDetailEditItem;
  static messageOnSuccess = 'The PlanningDetail Item has been saved.';

  static serviceFunction = async (item) => {
    // A Replaced period belongs to a pricing that a newer approved pricing superseded. It is kept as a
    // read-only record, and saving it here would send Status = ClientConfirmed and revive it. The backend
    // refuses the same thing; this just says why before the round trip.
    if (item.Status === PlanningStatusEnum.Replaced) {
      throw Object.assign(
        new Error('This fieldwork period belongs to a pricing that has been replaced and cannot be edited.'),
        { isBusinessError: true }
      );
    }

    const { ExtendEndDate, ApplyTargetRates, TargetAssignmentRate, TargetCompletionRate, ...payload } = item;

    // Saving an edit confirms the plan, except on a period whose fieldwork is already running —
    // that one keeps On Field rather than being pulled back a step.
    payload.Status =
      item.Status === PlanningStatusEnum.OnField ? PlanningStatusEnum.OnField : PlanningStatusEnum.ClientConfirmed;

    if (!ExtendEndDate) {
      payload.ExtendedEndDate = null;
      payload.TpRunId = null;
      payload.WaveId = null;
    } else {
      payload.ExtendedEndDate = dateTimeHelper.toYYYYsMMsDD(item.ExtendedEndDate, '-');
      if (isTpPlatform(item)) {
        payload.TpRunId = item.TpRunId ? parseInt(item.TpRunId, 10) : null;
        payload.WaveId = null;
      } else {
        payload.WaveId = item.WaveId ? parseInt(item.WaveId, 10) : null;
        payload.TpRunId = null;
      }
    }

    if (ApplyTargetRates) {
      if (mathHelper.Sum(TargetAssignmentRate) === 100) {
        payload.TargetAssignmentRate1 = TargetAssignmentRate[0];
        payload.TargetAssignmentRate2 = TargetAssignmentRate[1];
        payload.TargetAssignmentRate3 = TargetAssignmentRate[2];
        payload.TargetAssignmentRate4 = TargetAssignmentRate[3];
      }
      if (mathHelper.Sum(TargetCompletionRate) === 100) {
        payload.TargetCompletionRate1 = TargetCompletionRate[0];
        payload.TargetCompletionRate2 = TargetCompletionRate[1];
        payload.TargetCompletionRate3 = TargetCompletionRate[2];
        payload.TargetCompletionRate4 = TargetCompletionRate[3];
      }
    }

    await PlanningDetailsItemService.EditPlanningDetailsItem(payload);
    return true;
  };

  static pagesInGeneralEditWizzard = [
    {
      label: 'Dates & Extension',
      fields: [
        {
          fieldName: 'StartDate',
          label: 'Start Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          disabled: datesLocked,
          info: dateLockInfo,
          validityChecker: (value, item) => {
            if (item && item.IsDateLocked) return '';
            return value ? '' : 'Please select a start date.';
          },
          onFieldChange: (item) => {
            if (item.StartDate && item.EndDate && item.EndDate <= item.StartDate) {
              item.EndDate = shiftDays(item.StartDate, 1);
            }
          },
        },
        {
          fieldName: 'EndDate',
          label: 'End Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          disabled: datesLocked,
          info: dateLockInfo,
          validityChecker: (value, item) => {
            if (item && item.IsDateLocked) return '';
            if (!value) return 'Please select an end date.';
            if (item && item.StartDate && value <= item.StartDate) {
              return 'The end date must be after the start date.';
            }
            return '';
          },
          onFieldChange: (item) => {
            if (item.StartDate && item.EndDate && item.StartDate >= item.EndDate) {
              item.StartDate = shiftDays(item.EndDate, -1);
            }
          },
        },
        {
          fieldName: 'NumberOfPlannedAssignments',
          label: 'Number of Planned Assignments',
          className: 'col-12',
          type: InputTypesEnum.Number,
          validityChecker: (value) => {
            const parsed = Number(value);
            if (value === '' || isNaN(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
              return 'The value must be an integer greater than 0.';
            }
            return '';
          },
        },
        {
          fieldName: 'ExtendEndDate',
          label: 'Extend End Date',
          className: 'col-6',
          type: InputTypesEnum.OnOff,
          onFieldChange: (item) => {
            if (item.ExtendEndDate) {
              if (!item.ExtendedEndDate) item.ExtendedEndDate = item.EndDate;
            } else {
              item.ExtendedEndDate = null;
              item.TpRunId = null;
              item.WaveId = null;
            }
          },
        },
        {
          fieldName: 'ExtendedEndDate',
          label: 'Extended End Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          hide: (item) => !item.ExtendEndDate,
          validityChecker: (value, item) => {
            if (!item || !item.ExtendEndDate) return '';
            if (!value) return 'Please select an extended end date.';
            return value >= item.EndDate ? '' : 'The extended end date must be on or after the end date.';
          },
        },
        {
          fieldName: 'TpRunId',
          label: 'Run',
          className: 'col-6',
          type: InputTypesEnum.TpRunSelect,
          idFilter: 'PricingId',
          hide: (item) => !item.ExtendEndDate || !isTpPlatform(item),
          validityChecker: (value) => (value ? '' : 'Please select a run.'),
        },
        {
          fieldName: 'WaveId',
          label: 'Wave',
          className: 'col-6',
          type: InputTypesEnum.WaveSelect,
          idFilter: 'ClientId',
          hide: (item) => !item.ExtendEndDate || isTpPlatform(item),
          validityChecker: (value) => (value ? '' : 'Please select a wave.'),
        },
      ],
    },
    {
      label: 'Target Rates',
      fields: [
        {
          fieldName: 'ApplyTargetRates',
          label: 'Apply Target Rates',
          className: 'col-6',
          type: InputTypesEnum.OnOff,
        },
        {
          fieldName: 'TargetAssignmentRate',
          label: 'Target Assignment Rate (%)',
          className: 'col-6',
          type: InputTypesEnum.TargetRate,
          disabled: rateFieldsDisabled,
          validityChecker: rateValidityChecker,
        },
        {
          fieldName: 'TargetCompletionRate',
          label: 'Target Completion Rate (%)',
          className: 'col-6',
          type: InputTypesEnum.TargetRate,
          disabled: rateFieldsDisabled,
          validityChecker: rateValidityChecker,
        },
      ],
    },
  ];
}
